import { cn } from "@/lib/utils";
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { trackCtaClick, type AnalyticsCta } from "@/lib/analytics";

/** Map a CTA href to its funnel step for analytics. */
function ctaFromHref(href: string): AnalyticsCta | undefined {
  if (href.includes("visibility-audit") || href.includes("audit")) return "visibility_audit";
  if (href.includes("round-table") || href.includes("kcn-join-our-team")) return "round_table";
  if (href.includes("strategy-call") || href.includes("strategy")) return "strategy_session";
  if (href.includes("contact")) return "contact";
  if (href.includes("/blog")) return "blog";
  return undefined;
}

type Variant = "primary" | "ghost" | "outline";
type Size = "md" | "lg";

const base =
  "inline-flex items-center justify-center gap-2 font-medium tracking-wide uppercase text-xs transition-all duration-300 ease-out group/btn whitespace-nowrap";

const variants: Record<Variant, string> = {
  primary:
    "bg-primary text-primary-foreground hover:bg-primary/90 border border-primary",
  outline:
    "border border-foreground/40 text-foreground hover:bg-foreground hover:text-background",
  ghost: "text-foreground hover:text-primary",
};

const sizes: Record<Size, string> = {
  md: "px-5 py-3",
  lg: "px-7 py-4 text-[0.78rem]",
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
  arrow?: boolean;
  children: ReactNode;
  className?: string;
}

export function CTAButton({
  href,
  external,
  variant = "primary",
  size = "md",
  arrow = true,
  children,
  className,
  onClick,
  ...rest
}: CommonProps & { href: string; external?: boolean } & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    "children" | "href" | "className"
  >) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className={cn(base, variants[variant], sizes[size], className)}
      onClick={(e) => {
        const cta = ctaFromHref(href);
        if (cta) trackCtaClick(cta, typeof window !== "undefined" ? window.location.pathname : "/");
        onClick?.(e);
      }}
      {...rest}
    >
      <span>{children}</span>
      {arrow ? (
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
      ) : null}
    </a>
  );
}

export function PlainButton({
  variant = "primary",
  size = "md",
  arrow = false,
  children,
  className,
  ...rest
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={cn(base, variants[variant], sizes[size], className)} {...rest}>
      <span>{children}</span>
      {arrow ? <ArrowUpRight className="h-4 w-4" /> : null}
    </button>
  );
}
